import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth, API } from "@/App";
import axios from "axios";

/**
 * UsageWarningBanner - SLICE 5
 * 
 * Displays usage warnings and limit reached notifications.
 * Shows when user is at 80%+ of any limit.
 * 
 * Usage:
 *   <UsageWarningBanner />
 * 
 * Place this in the main layout to show warnings across all pages.
 */
export default function UsageWarningBanner() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUsage();
    }
  }, [token]);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/business/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch usage", error);
    } finally {
      setLoading(false);
    }
  };

  const dismissWarning = (type) => {
    setDismissed(prev => new Set([...prev, type]));
    // Store in sessionStorage to persist dismissal during session
    const dismissedKey = `usage_warning_dismissed_${type}`;
    sessionStorage.setItem(dismissedKey, "true");
  };

  // Check sessionStorage for previously dismissed warnings
  useEffect(() => {
    const types = ["forms", "submissions"];
    const previouslyDismissed = new Set();
    types.forEach(type => {
      if (sessionStorage.getItem(`usage_warning_dismissed_${type}`)) {
        previouslyDismissed.add(type);
      }
    });
    setDismissed(previouslyDismissed);
  }, []);

  if (loading || !usage) {
    return null;
  }

  // Filter out dismissed and internal warnings
  const activeWarnings = (usage.usage_warnings || []).filter(
    w => !w.internal && !dismissed.has(w.type)
  );
  
  const activeLimits = (usage.limits_reached || []).filter(
    l => !l.internal && !dismissed.has(l.type)
  );

  // Nothing to show
  if (activeWarnings.length === 0 && activeLimits.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 mb-4" data-testid="usage-warning-banner">
      {/* Limits Reached - More prominent */}
      {activeLimits.map((limit, index) => (
        <div 
          key={`limit-${limit.type}-${index}`}
          className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-xl"
          data-testid={`limit-reached-${limit.type}`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-100">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="font-semibold text-red-800">
                {limit.type === "forms" && "Form Limit Reached"}
                {limit.type === "submissions" && "Submission Limit Reached"}
              </p>
              <p className="text-sm text-red-600">{limit.message}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => navigate("/settings?tab=billing")}
              data-testid={`upgrade-btn-${limit.type}`}
            >
              <Zap className="w-4 h-4 mr-1" />
              Upgrade Now
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-red-600 hover:text-red-800"
              onClick={() => dismissWarning(limit.type)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      
      {/* Warnings - Less prominent */}
      {activeWarnings.map((warning, index) => (
        <div 
          key={`warning-${warning.type}-${index}`}
          className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-xl"
          data-testid={`usage-warning-${warning.type}`}
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="p-1.5 rounded-full bg-amber-100">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-800">{warning.message}</p>
              <div className="mt-1 max-w-xs">
                <Progress 
                  value={warning.percentage} 
                  className="h-1.5 bg-amber-100" 
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              onClick={() => navigate("/settings?tab=billing")}
            >
              View Plans
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-amber-600 hover:text-amber-800"
              onClick={() => dismissWarning(warning.type)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
